import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Loader2, ExternalLink, Phone, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAGARO_URL = "https://www.vagaro.com/dbhgg6";

const timeSlots = [
  "7:00 AM",
  "8:00 AM",
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
];

const planOptions = [
  { value: "weekly", label: "Weekly Elite - $250/mo" },
  { value: "biweekly", label: "Bi-Weekly Pro - $140/mo" },
  { value: "monthly", label: "Monthly Essentials - $100/mo" },
];

export default function LeadCapture() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    plan: "",
    preferred_time: "",
    notes: "",
  });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.phone || !form.zip_code || !form.plan) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (!selectedDate) {
      toast.error("Please select a preferred date.");
      return;
    }

    if (!form.preferred_time) {
      toast.error("Please select a preferred time.");
      return;
    }

    setLoading(true);
    try {
      // Save lead
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        zip_code: form.zip_code,
        recommended_plan: form.plan,
        source: "booking_form",
      });

      // Save booking
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        zip_code: form.zip_code,
        preferred_date: selectedDate.toISOString().split("T")[0],
        preferred_time: form.preferred_time,
        plan: form.plan,
        notes: form.notes,
      });

      setSuccess(true);
      toast.success("Booking submitted! We'll confirm shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section data-testid="booking-success" className="relative py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24 max-w-2xl mx-auto text-center">
          <div className="p-10 rounded-md bg-[#141416] border border-[#C5A059]/30 glow-gold">
            <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-4">
              You're all set!
            </h2>
            <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-6">
              We've received your info. Complete your booking on our scheduling platform and a LUXGROOM specialist will confirm your appointment.
            </p>

            <a
              href={VAGARO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="vagaro-booking-link"
              className="inline-flex items-center gap-2 bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider mb-6"
            >
              Complete Booking on Vagaro
              <ExternalLink className="w-4 h-4" />
            </a>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4">
              <a href="tel:4159946699" className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C5A059] transition-colors">
                <Phone className="w-4 h-4" /> (415) 994-6699
              </a>
              <a href="mailto:ltaplett@luxgroom.net" className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#C5A059] transition-colors">
                <Mail className="w-4 h-4" /> ltaplett@luxgroom.net
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="booking-section" className="relative py-24 md:py-32 bg-[#0A0A0B]">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
              Book Your First Cut
            </span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-4">
            Start your membership today
          </h2>
          <p className="text-base text-[#A1A1AA] leading-relaxed font-light">
            Share your details below and we'll match you with your dedicated barber. Or book directly on{" "}
            <a href={VAGARO_URL} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:text-[#D4AF37] underline underline-offset-2" data-testid="inline-vagaro-link">
              Vagaro
            </a>.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-8 max-w-5xl">
          {/* Left - Form Fields */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Full Name *</label>
              <input
                data-testid="booking-name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Marcus Thompson"
                className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Email *</label>
              <input
                data-testid="booking-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="marcus@company.com"
                className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Phone *</label>
              <input
                data-testid="booking-phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(415) 555-0123"
                className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Zip Code *</label>
              <input
                data-testid="booking-zip"
                type="text"
                name="zip_code"
                value={form.zip_code}
                onChange={handleChange}
                placeholder="94105"
                className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Select Plan *</label>
              <Select value={form.plan} onValueChange={(val) => setForm({ ...form, plan: val })}>
                <SelectTrigger
                  data-testid="booking-plan-select"
                  className="w-full bg-white/5 border-white/10 text-white h-12 rounded-sm focus:ring-1 focus:ring-[#C5A059]"
                >
                  <SelectValue placeholder="Choose your membership" />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/10">
                  {planOptions.map((p) => (
                    <SelectItem
                      key={p.value}
                      value={p.value}
                      data-testid={`booking-plan-option-${p.value}`}
                      className="text-[#F3F4F6] focus:bg-[#C5A059]/10 focus:text-[#F3F4F6]"
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Notes (optional)</label>
              <textarea
                data-testid="booking-notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any preferences, gate codes, parking info..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all px-4 py-3 placeholder:text-[#71717A] outline-none resize-none"
              />
            </div>
          </div>

          {/* Right - Calendar + Time + Submit */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm text-[#A1A1AA] mb-3 font-medium">Preferred Date *</label>
              <div className="inline-block rounded-md bg-[#141416] border border-white/10 p-2">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  data-testid="booking-calendar"
                  className="text-[#F3F4F6]"
                  classNames={{
                    day_selected: "bg-[#C5A059] text-black hover:bg-[#D4AF37] focus:bg-[#C5A059]",
                    day_today: "bg-white/10 text-[#F3F4F6]",
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Preferred Time *</label>
              <Select value={form.preferred_time} onValueChange={(val) => setForm({ ...form, preferred_time: val })}>
                <SelectTrigger
                  data-testid="booking-time-select"
                  className="w-full bg-white/5 border-white/10 text-white h-12 rounded-sm focus:ring-1 focus:ring-[#C5A059]"
                >
                  <SelectValue placeholder="Select a time slot" />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-white/10">
                  {timeSlots.map((time) => (
                    <SelectItem
                      key={time}
                      value={time}
                      className="text-[#F3F4F6] focus:bg-[#C5A059]/10 focus:text-[#F3F4F6]"
                    >
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button
              data-testid="booking-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Book My First Cut"
              )}
            </button>

            <p className="text-xs text-[#71717A] text-center">
              By submitting, you agree to be contacted by LUXGROOM about your membership.
              We'll never share your information. Questions? Call{" "}
              <a href="tel:4159946699" className="text-[#C5A059] hover:text-[#D4AF37]">(415) 994-6699</a>.
            </p>
          </div>
        </form>
      </div>

      <div className="section-divider mt-24 mx-6 md:mx-12 lg:mx-24" />
    </section>
  );
}
